import { ExtendedContainer, ExtendedSprite, ExtendedText, gsap } from "@aceroll/core";
import { ORIENTATIONS } from "../../constants";

const CARDS_CONFIG = [
    {
        name: "Echospins",
        title: "echospins_uc",
        titleStyle: "splashFeatureTitle",
        description: "about_echospins_short_uc",
        descriptionStyle: "splashFeatureDescription",
        image: "splash-card_big",
        icon: "splash-icon_echospin",
        iconScale: 1.05,
        iconPosition: { x: -11, y: -174 }
    },
    {
        name: "Bonus games",
        title: "bonus_games_uc",
        titleStyle: "splashFeatureTitle",
        description: "about_bonus_games_short_uc",
        descriptionStyle: "splashFeatureDescription",
        image: "splash-card_big",
        icon: "splash-icon_bonus",
        iconScale: 1.2,
        iconPosition: { x: 0, y: -130 }
    },
    {
        name: "Max Win",
        title: "max_win_uc",
        titleStyle: "splashFeatureTitle",
        description: "about_max_win_uc",
        descriptionStyle: "splashFeatureDescription",
        image: "splash-card_big",
        icon: "splash-icon_max_win",
        iconScale: 0.96,
        iconPosition: { x: 0, y: -160 }
    }
]

const CARDS_POSITIONS = {
    LANDSCAPE: [0, 514, 1018],
    PORTRAIT: [-1400, 230, 1630]
}

const CONTENT_SCALE = {
    LANDSCAPE: 0.95,
    PORTRAIT: 0.85
}

export default class SplashPopup extends ExtendedContainer {
    constructor(orientation) {
        super('splash-popup');

        this._cards = [];
        this._content = new ExtendedContainer('Content Container');
        this._orientation = orientation;
        this._currentScale = null;

        this.createLogo();
        this.addVolatilityLabel();
        this.createCardsContainers();
        this.createCards();
        this.placeCards();
        this.addClickLabel();
        this.addChild(this._content);
    }

    createCardsContainers() {
        this._landscapeCardsContainer = this._content.addChild(new ExtendedContainer('Landscape Cards'));
        this._landscapeCardsContainer.position.set(-514, 138);

        this._portraitCardsContainer = this._content.addChild(new ExtendedContainer('Portrait Cards'));
        this._portraitCardsContainer.position.set(-235, 212);
    }

    createCards() {
        for (const cardConfig of CARDS_CONFIG) {
            const card = this.createCard(cardConfig)
            this._cards.push(card);
        }
    }

    createCard(config) {
        const card = ExtendedSprite.fromSpriteSheet(config.image);
        card.anchor.set(0.5, 0.5);
        card.name = config.name;
        const icon = card.addChild(ExtendedSprite.fromSpriteSheet(config.icon))
        const title = card.addChild(new ExtendedText(config.name, config.title, config.titleStyle));
        const description = card.addChild(new ExtendedText(config.name, config.description, config.descriptionStyle));

        icon.anchor.set(0.5, 0.5);
        icon.position.set(config.iconPosition.x, config.iconPosition.y);
        icon.scale.set(config.iconScale);

        title.anchor.set(0.5, 0.5);
        title.position.set(0, 160);

        description.anchor.set(0.5, 0);
        description.position.set(0, 240);

        return card;
    }

    createLogo() {
        this._logo = ExtendedSprite.fromSpriteSheet('splash-logo');
        this._logo.name = 'Logo';
        this._logo.anchor.set(0.5, 0.5);

        this._content.addChild(this._logo);
    }

    addVolatilityLabel() {
        this._volatilityLabel = ExtendedSprite.fromSpriteSheet('splash-volatility');
        this._volatilityLabel.name = 'Volatility label';
        this._volatilityLabel.anchor.set(0.5, 0.5);

        this._content.addChild(this._volatilityLabel);
    }

    addClickLabel() {
        this._clickLabel = new ExtendedText('Click', 'click_to_continue_uc', 'splashContinueLabel');
        // this._clickLabel.name = 'Click';
        this._clickLabel.anchor.set(0.5, 0.5);
        this._clickLabel.position.set(0, 846);

        this._content.addChild(this._clickLabel);
    }

    scaleContent() {
        this.clearTimeline(this._contentScaleTimeline);

        this._contentScaleTimeline = gsap.timeline();
        const { scaleFrom, scaleTo } = this.contentScale;
        this._content.scale.set(scaleFrom);

        this._contentScaleTimeline
            .to(this._content.scale, {
                x: scaleTo,
                y: scaleTo,
                duration: 0.5,
                onComplete: () => {
                    this._currentScale = scaleTo;
                }
            });
    }

    placeCards() {
        if (this._orientation === ORIENTATIONS.LANDSCAPE) {
            this._portraitCardsContainer.removeChildren();
            this.updateCardsStartPosition(this._landscapeCardsContainer);
        } else if (this._orientation === ORIENTATIONS.PORTRAIT) {
            this._landscapeCardsContainer.removeChildren();
            this.updateCardsStartPosition(this._portraitCardsContainer);
        } else {
            throw new Error('Set proper orientation! Current orientation is: ', this._orientation);
        }
    }

    startPortraitCardSequence() {
        this.clearTimeline(this._portraitCardTimeline);

        this._portraitCardTimeline = gsap.timeline({
            repeat: -1,
            repeatDelay: 0
        });

        this.setupCardSequence();
    }

    setupCardSequence() {
        const timeline = this._portraitCardTimeline;
        const cards = [...this._cards];
        const centerX = CARDS_POSITIONS.PORTRAIT[1];
        const rightX = CARDS_POSITIONS.PORTRAIT[2];
        const leftX = CARDS_POSITIONS.PORTRAIT[0];

        cards.forEach((card) => card.x = leftX);

        let currentIndex = cards.length - 1;
        cards[currentIndex].x = centerX;

        for (let i = 0; i < cards.length; i++) {
            const currentCard = cards[currentIndex];
            const nextIndex = (currentIndex - 1 + cards.length) % cards.length;
            const nextCard = cards[nextIndex];

            timeline
                .to(currentCard, {
                    delay: 1.5
                })
                .to(currentCard, {
                    x: rightX,
                    duration: 0.5,
                    onComplete: () => {
                        currentCard.x = leftX;
                    }
                })
                .to(nextCard, {
                    x: centerX,
                    duration: 0.5
                })

            currentIndex = nextIndex;
        }
    }

    updateCardsStartPosition(container) {
        this._cards.forEach((card, i) => {
            const scale = this._orientation === ORIENTATIONS.LANDSCAPE ? 0.55 : 1;
            card.scale.set(scale);

            container.addChild(card);
            card.x = CARDS_POSITIONS[this._orientation.toUpperCase()][i];
        });
    }

    setLandscape() {
        this._orientation = ORIENTATIONS.LANDSCAPE;
        this.scaleContent();
        this._volatilityLabel.scale.set(0.72);
        this._volatilityLabel.position.set(0, -215);
        this._logo.scale.set(0.59);
        this._logo.position.set(0, -384);
        this._clickLabel.position.set(0, 488);
        this.clearTimeline(this._portraitCardTimeline);
        this.placeCards();
    };

    setPortrait() {
        this._orientation = ORIENTATIONS.PORTRAIT;
        this.scaleContent();
        this._volatilityLabel.scale.set(1);
        this._volatilityLabel.position.set(0, -418);
        this._logo.scale.set(1);
        this._logo.position.set(-4, -699);
        this._clickLabel.position.set(0, 846);
        this.placeCards();
        this.startPortraitCardSequence();
    }

    clearTimeline(timeline) {
        if (timeline) {
            timeline.kill();
            timeline = null;
        }
    }

    hide() {
        this.clearTimeline(this._portraitCardTimeline);
        this.clearTimeline(this._contentScaleTimeline);
    }

    get contentScale() {
        const isLandscape = this._orientation === ORIENTATIONS.LANDSCAPE;
        const baseScaleFrom = isLandscape ? CONTENT_SCALE.PORTRAIT : CONTENT_SCALE.LANDSCAPE;
        const scaleFrom = this._currentScale ? this._currentScale : baseScaleFrom;
        const scaleTo = isLandscape ? CONTENT_SCALE.LANDSCAPE : CONTENT_SCALE.PORTRAIT;

        return { scaleFrom, scaleTo };
    }
}